import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject } from 'aurelia-framework';
import httpStatus from 'http-status';

import { GenericApi } from '~/api/generic-api';
import { LoginApi } from '~/api/login-api';
import { App, AppEvents } from '~/app';
import { Authentication } from '~/authentication/authentication';
import { TREE_VIEW_SESSION_VARIABLE_NAME } from '~/constants';
import { LoginConstants } from '~/login/login-constants';
import { OktaLogin } from '~/login/okta-login';
import { Interceptor } from '~/utils/fetch-utils';
import { removeItem, setItem } from '~/utils/local-storage';
import { I18N } from 'aurelia-i18n';

interface RequestWithCopy extends Request {
	_copy?: Request;
}

@autoinject
export class AuthInterceptor implements Interceptor {
	constructor(
		private readonly loginApi: LoginApi,
		private readonly eventAggregator: EventAggregator,
		private readonly authentication: Authentication,
		private readonly i18n: I18N,
	) {
		this.eventAggregator.subscribe(
			AppEvents.TokenRefreshed,
			(isRefreshed: boolean) => {
				this.authentication.setIsTokenRefreshing(false);
				const errorMessage = this.i18n.tr(
					'errors.authentication-error',
				);
				if (!isRefreshed) {
					// Refresh failed. Log out.
					// We can also get here when there are problems with the okta login callback,
					// since we don't have an effective way to tell whether okta login failed or has expired
					this.eventAggregator.publish(
						App.Events.Logout,
						errorMessage,
					);
				}
			},
		);

		// Setup hooks
		removeItem(LoginConstants.UNLOADING_PAGE);
		window.addEventListener('beforeunload', () => {
			if (this.authentication.isTokenRefreshing()) {
				navigator.sendBeacon(
					GenericApi.apiServerURL + 'oauth/token/log',
					"reloading page when refresh token request fired but response hasn't been processed yet",
				);
				// NOTE(Jae): 2020-04-02
				// Asked York why we needed this, his reply was:
				// We need to wait 200ms otherwise the E2E tests can fallover
				// due to token issues. Ideally we'd have a better solution thats
				// solved on the E2E side, but for now, leave this in.
				const startTime = new Date().getTime();
				// tslint:disable-next-line:curly
				while (new Date().getTime() < startTime + 200);
				this.authentication.setIsTokenRefreshing(false);
			}
			setItem(LoginConstants.UNLOADING_PAGE, 'true');
		});
	}

	/**
	 * If logged in, add authorization header to the request
	 * @override
	 */
	public request(
		request: RequestWithCopy,
	): Request | Response | Promise<Request | Response> {
		if (this.authentication.isLoggedIn()) {
			if (!OktaLogin.isLoggedIn()) {
				request = this.getRequestWithToken(request) as RequestWithCopy;
			} else if (OktaLogin.oktaAuthOptionsAreSet()) {
				// auth options may not be set even when logged in if we are opening a form in a new tab
				// return early because Okta login does not need any refresh token handling
				return this.getRequestWithToken(request);
			}
		}

		// If access token is under refreshing and this request is not a refresh token request, delay it until token refreshed.
		const isRefreshing = this.authentication.isTokenRefreshing();
		if (isRefreshing && !this.loginApi.isRefreshRequest(request.url)) {
			return new Promise<Request>((resolve, reject) => {
				this.eventAggregator.subscribeOnce(
					AppEvents.TokenRefreshed,
					(refreshedResult) => {
						if (refreshedResult) {
							resolve(this.getRequestWithToken(request));
						} else {
							reject({
								error: 'invalid_grant',
								message: 'Refresh access token failed.',
							});
						}
					},
				);
			});
		}
		// Note: York - WEBUI-2783
		// Native fetch and Request object don't allow to reuse a request whose body is used already. When fetch(request)
		// is called and get 401, we can't retry fetch(request) with refreshed access token since the body is used. Clone
		// a copy and store it in original request for later usage.
		request._copy = request.clone();
		return request;
	}

	/**
	 * Aurelia Fetch Client will crash with "Failed to fetch" error without this
	 * method.
	 * - https://stackoverflow.com/a/46194803/5013410
	 * @override
	 */
	public responseError(
		error: any | Response,
		request: RequestWithCopy,
	): Response | Promise<Response> {
		if (error && error.status === httpStatus.UNAUTHORIZED) {
			// retry request after token is refreshed
			const retryRequestPromise: Promise<Response> = new Promise<void>(
				(resolve, reject) => {
					this.eventAggregator.subscribeOnce(
						AppEvents.TokenRefreshed,
						(refreshedResult: boolean) => {
							if (refreshedResult) {
								resolve();
							} else {
								reject({
									error: 'invalid_grant',
									message: 'Refresh access token failed.',
								});
							}
						},
					);
				},
			).then((): Promise<Response> => {
				// Note: York - WEBUI-2783
				// Can't use the original request if request has body used. Use its clone instead.
				const requestCopy = this.getRequestWithToken(
					request._copy,
				) as Request;
				// NOTE(Jae): 2020-03-04
				// We just use regular "fetch" here as we don't want any custom error handling or
				// interceptors to interfere.
				return fetch(requestCopy);
			});

			const isTreeViewLoggingInForFirstTime =
				sessionStorage.getItem(TREE_VIEW_SESSION_VARIABLE_NAME) ===
					'1' && !LoginApi.getAccessToken();

			// NOTE(Albert): 2020-10-13 - WEBUI-3579
			// If we're in Tree View, we will reach this path because we have skipped the Authorize step before we
			// have actually finished logging in. In this scenario, there is no need to try to refresh the token because
			// when the first WEBUI form is loaded, we will already be logged in and we avoid this path completely
			if (
				!this.authentication.isTokenRefreshing() &&
				!isTreeViewLoggingInForFirstTime
			) {
				if (!this.authentication.isLoggedIn()) {
					// We are not logged in. Logout and redirect to the login page.
					this.eventAggregator.publish(App.Events.Logout);
				}
				this.authentication.tryToRefreshToken(request.url);
			}

			return retryRequestPromise;
		}
		return error;
	}

	private getRequestWithToken(
		request: RequestWithCopy,
	): Promise<RequestWithCopy> | RequestWithCopy {
		const accessToken = this.authentication.getLocalOrOktaAccessToken();
		if (accessToken instanceof Promise) {
			return new Promise<Request>((resolve, reject) => {
				const ssoLoginTokenError = {
					error: 'invalid_grant',
					message: 'Unable to retrieve Access Token for SSO Login',
					status: httpStatus.INTERNAL_SERVER_ERROR,
				};
				accessToken
					.then((token) => {
						if (token) {
							this.addAccessTokenToRequest(request, token);
							request._copy = request.clone();
							resolve(request);
						} else {
							reject(ssoLoginTokenError);
						}
					})
					.catch(() => reject(ssoLoginTokenError));
			});
		} else {
			this.addAccessTokenToRequest(request, accessToken);
			return request;
		}
	}

	private addAccessTokenToRequest(
		request: Request,
		accessToken: string,
	): void {
		request.headers.set('Authorization', `Bearer ${accessToken}`);
	}
}
